import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  type DayRange,
  dayToIso,
  isoToDay,
  type PresetKey,
  presetRange,
  PRESETS,
  previousPeriod,
} from './dateRange';

function isValidDate(value: string | null): value is string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const day = isoToDay(value);
  return Number.isFinite(day) && dayToIso(day) === value;
}

const DEFAULT_PRESET: PresetKey = '30d';

export interface DateRangeState {
  preset: PresetKey;
  range: DayRange;
  prev: DayRange;
  customFromIso: string;
  customToIso: string;
  setPreset: (k: PresetKey) => void;
  setCustom: (fromIso: string, toIso: string) => void;
}

/** Global report date range, persisted in the URL (`?preset=30d` or `?preset=custom&from=&to=`)
 *  so a filtered report is shareable / bookmarkable (deep-linking). */
export function useDateRange(): DateRangeState {
  const [params, setParams] = useSearchParams();
  const rawPreset = params.get('preset');
  const preset =
    PRESETS.find((item) => item.key === rawPreset)?.key ?? DEFAULT_PRESET;
  const fromIso = params.get('from');
  const toIso = params.get('to');

  const range = useMemo<DayRange>(() => {
    if (preset === 'custom' && isValidDate(fromIso) && isValidDate(toIso)) {
      const a = isoToDay(fromIso),
        b = isoToDay(toIso);
      return a <= b ? { fromDay: a, toDay: b } : { fromDay: b, toDay: a };
    }
    return presetRange(preset === 'custom' ? '30d' : preset);
  }, [preset, fromIso, toIso]);

  const prev = useMemo(() => previousPeriod(range), [range]);

  const setPreset = (k: PresetKey) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        n.set('preset', k);
        if (k !== 'custom') {
          n.delete('from');
          n.delete('to');
        }
        return n;
      },
      { replace: true },
    );

  const setCustom = (f: string, t: string) =>
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        n.set('preset', 'custom');
        n.set('from', f);
        n.set('to', t);
        return n;
      },
      { replace: true },
    );

  return {
    preset,
    range,
    prev,
    setPreset,
    setCustom,
    customFromIso: dayToIso(range.fromDay),
    customToIso: dayToIso(range.toDay),
  };
}
